import {
  PathPickOrder,
  PathPickOrderDetailResponse,
  PathPickOrderItem,
  PathPickOrderItemOption,
  PathPickOrderRemittance,
} from "@/features/pathpick/features/order-history/types/response";
import { PathPickOrderStatus } from "@/features/pathpick/features/order-history/types";
import {
  PathPickOrderDetailSectionKey,
  PathPickOrderRemittanceView,
  PathPickOrderShipmentView,
  PathPickOrderStep,
  PathPickOrderStepCode,
} from "@/features/pathpick/features/order-history/features/detail/types";
import {
  CANCEL_REASON_LABEL_KEYS,
  ORDER_DETAIL_KEY,
  ORDER_DETAIL_STEP_CODES,
  SHIPMENT_IN_TRANSIT_STATUSES,
} from "@/features/pathpick/features/order-history/features/detail/constants";
import {
  formatPathPickOrderDateTime,
  isPathPickOrderQuantityConfirmed,
  selectPathPickOrderAmount,
} from "@/features/pathpick/features/order-history/utils";

// 취소 수량은 수량 확정 외에 "주문 취소"로도 결정된다. 취소된 주문은 quantityConfirmedAt이
// 비어 있고 canceledAt만 찍히므로 확정 플래그만 보면 취소 수량이 영영 "확인 중"에 머문다.
export const isPathPickOrderCancelDetermined = (order: PathPickOrder) =>
  isPathPickOrderQuantityConfirmed(order) ||
  Boolean(order.canceledAt) ||
  (order.totalCanceledQuantity ?? 0) > 0;

// 상품 단위 수량 확정 여부. 수량 확인 중에도 상품은 개별 확정되므로 상품 플래그가 우선이다.
// 주문 단위 확정은 폴백 — is_quantity_confirmed가 없던 시절 주문은 확정 시각으로만 판별할 수 있고,
// 주문이 확정됐다면 모든 상품이 확정된 것이기도 하다.
export const isPathPickOrderItemQuantityConfirmed = (
  order: PathPickOrder,
  item: PathPickOrderItem
) => item.isQuantityConfirmed || isPathPickOrderQuantityConfirmed(order);

// isPathPickOrderCancelDetermined의 상품 단위 판. 주문 합계(totalCanceledQuantity) 대신
// 이 상품의 취소 수량을 보므로, 다른 상품만 확정된 상태에서 이 상품이 "취소 0"으로 단정되지 않는다.
export const isPathPickOrderItemCancelDetermined = (
  order: PathPickOrder,
  item: PathPickOrderItem
) =>
  isPathPickOrderItemQuantityConfirmed(order, item) ||
  Boolean(order.canceledAt) ||
  item.canceledQuantity > 0;

// 요약 스트립의 금액 셀. 타이틀(요청 기준/최종)과 값을 같은 조건으로 정해야 둘이 어긋나지 않는다.
export const selectPathPickOrderSummaryAmount = (order: PathPickOrder) => {
  const amount = selectPathPickOrderAmount(order);

  if (isPathPickOrderQuantityConfirmed(order)) {
    return {
      titleKey: `${ORDER_DETAIL_KEY}final_amount`,
      badgeKey: null,
      amount,
      // 시안: 확정 후에는 주문(요청) 금액을 취소선으로 확정 금액 위에 같이 남긴다.
      struckAmount: order.totalOrderedAmount ?? null,
      noteKey: `${ORDER_DETAIL_KEY}note_final`,
    };
  }
  return {
    titleKey: `${ORDER_DETAIL_KEY}requested_amount`,
    badgeKey: `${ORDER_DETAIL_KEY}badge_before_confirm`,
    amount,
    struckAmount: null,
    noteKey: `${ORDER_DETAIL_KEY}note_confirm_pending`,
  };
};

// "우리은행 1005-804-502502" — 은행/계좌 중 하나라도 없으면 표기하지 않는다.
export const formatPathPickRemittanceAccount = (
  remittance: PathPickOrderRemittance
) =>
  remittance.bankName && remittance.accountNumber
    ? `${remittance.bankName} ${remittance.accountNumber}`
    : null;

// 입금 정보 카드 전체를 한 번에 고른다. 컴포넌트는 분기 없이 그리기만 하도록.
export const selectPathPickOrderRemittanceView = (
  order: PathPickOrder
): PathPickOrderRemittanceView => {
  const { remittance } = order;
  // 입금 요청액이 없으면 주문(요청) 기준 금액으로 대체한다.
  const remittedAmount = remittance.amount ?? order.totalOrderedAmount ?? 0;

  const common = {
    dueAtLabel: remittance.dueAt
      ? formatPathPickOrderDateTime(remittance.dueAt)
      : null,
    account: formatPathPickRemittanceAccount(remittance),
    accountHolder: remittance.accountHolder ?? null,
    remitterName: remittance.remitterName ?? null,
    completedAtLabel: remittance.completedAt
      ? formatPathPickOrderDateTime(remittance.completedAt)
      : null,
    // 카운트다운은 입금을 기다리는 동안에만 의미가 있다.
    autoCancelDueAt:
      order.status === "REMITTANCE_REQUESTED" ? remittance.dueAt ?? null : null,
  };

  // 수량 확정 후에는 입금액/최종 금액/환불 예정액 3열로 바뀐다.
  if (isPathPickOrderQuantityConfirmed(order)) {
    const finalAmount =
      order.totalConfirmedAmount ?? order.totalOrderedAmount ?? 0;

    return {
      ...common,
      amounts: {
        kind: "triple",
        remittedAmount,
        finalAmount,
        refundAmount: Math.max(0, remittedAmount - finalAmount),
        noteKey: `${ORDER_DETAIL_KEY}payment_note_refund`,
        isNoteHighlighted: true,
      },
    };
  }

  // 입금이 끝나고 수량을 확인하는 중이면 "입금 완료 금액"으로 라벨이 바뀐다.
  // (REMITTED는 명세상 미사용이지만 레거시 데이터가 내려올 수 있어 같이 본다)
  const isRemittanceDone =
    order.status === "QUANTITY_CHECKING" || order.status === "REMITTED";

  return {
    ...common,
    amounts: {
      kind: "single",
      labelKey: isRemittanceDone
        ? `${ORDER_DETAIL_KEY}payment_amount_completed`
        : `${ORDER_DETAIL_KEY}payment_amount`,
      amount: remittedAmount,
      noteKey: isRemittanceDone
        ? `${ORDER_DETAIL_KEY}payment_note_checking`
        : `${ORDER_DETAIL_KEY}payment_note_requested`,
      isNoteHighlighted: false,
    },
  };
};

// 상태 → 스텝퍼 단계. 명세상 미사용인 REQUESTED/REMITTED는 각각 입금 요청/수량 확인 단계로
// 흡수해 레거시 주문도 5단계 위에 얹힌다. CANCELED는 스텝퍼를 그리지 않는다.
const STATUS_TO_STEP_CODE: Record<
  PathPickOrderStatus,
  PathPickOrderStepCode | null
> = {
  REQUESTED: "REMITTANCE_REQUESTED",
  REMITTANCE_REQUESTED: "REMITTANCE_REQUESTED",
  REMITTED: "QUANTITY_CHECKING",
  QUANTITY_CHECKING: "QUANTITY_CHECKING",
  QUANTITY_CONFIRMED: "QUANTITY_CONFIRMED",
  SHIPPING: "SHIPPING",
  COMPLETED: "COMPLETED",
  CANCELED: null,
};

// 응답에 steps[]가 없어 현재 상태만으로 단계를 파생한다.
// 미매핑(취소) 상태는 전부 pending으로 그린다.
export const toPathPickOrderSteps = (
  order: PathPickOrder
): PathPickOrderStep[] => {
  const currentCode = STATUS_TO_STEP_CODE[order.status] ?? null;
  const currentIndex = currentCode
    ? ORDER_DETAIL_STEP_CODES.indexOf(currentCode)
    : -1;

  return ORDER_DETAIL_STEP_CODES.map((code, index) => {
    if (currentIndex < 0 || index > currentIndex) {
      return { code, state: "pending" as const };
    }
    if (index < currentIndex) return { code, state: "done" as const };
    // 배송 완료까지 끝난 주문은 마지막 단계도 진행 중이 아니라 완료로 그린다.
    return {
      code,
      state:
        order.status === "COMPLETED" ? ("done" as const) : ("current" as const),
    };
  });
};

// 배송 카드 목록 — 서버가 배송 건을 여러 개 내려주므로 그대로 매핑한다.
export const toPathPickOrderShipments = (
  order: PathPickOrder
): PathPickOrderShipmentView[] =>
  order.shipments.map((shipment) => ({
    id: shipment.id,
    status: shipment.status,
    courierName: shipment.courierName ?? null,
    trackingNumber: shipment.trackingNumber ?? null,
    expectedDeliveredOn: shipment.expectedDeliveredOn ?? null,
    items: shipment.items.map((item) => ({
      id: item.id,
      brandName: item.brandName,
      sku: item.sku,
      productName: item.productName,
      optionName: item.optionName,
      displayOptionName: item.displayOptionName,
      quantity: item.quantity,
    })),
  }));

// "배송 중"은 세분화된 네 단계(해외 배송/통관/국내 배송 대기/국내 배송)를 합산한다 —
// 특정 값 하나만 세면 단계가 나뉜 뒤로는 조용히 0이 된다.
export const summarizePathPickOrderShipments = (
  shipments: PathPickOrderShipmentView[]
) => ({
  total: shipments.length,
  delivered: shipments.filter((it) => it.status === "DELIVERED").length,
  shipping: shipments.filter((it) =>
    SHIPMENT_IN_TRANSIT_STATUSES.includes(it.status)
  ).length,
});

// KREAM 입찰 파일 카드가 쓰는 수치 묶음.
// 요청/대상/제외는 모두 "개"(수량) 기준이고 요청 = 대상 + 제외로 맞아떨어진다.
// 모달 요약("2종 · 총 3개")의 종은 확정 수량이 남은 상품 수라서 items에서 따로 센다.
export const selectPathPickOrderKreamFileView = (
  order: PathPickOrderDetailResponse
) => {
  // 확정 수량으로 만든 요청 id 목록 — 하나라도 있으면 이미 신청한 주문이다.
  const requestIds = order.requestIds ?? [];

  return {
    requestedQuantity: order.totalOrderedQuantity ?? 0,
    targetQuantity: order.totalConfirmedQuantity ?? 0,
    excludedQuantity: order.totalCanceledQuantity ?? 0,
    confirmedKindCount: order.items.filter((item) => item.confirmedQuantity > 0)
      .length,
    // 재신청 차단 판정 — 확정 수량 파일은 주문당 한 번만 신청한다.
    hasRequested: requestIds.length > 0,
    // 신청 상품 내역을 열 요청 — 정렬은 서버 몫이라 받은 순서의 첫 원소를 그대로 쓴다.
    requestId: requestIds[0] ?? null,
  };
};

// 취소 사유 code → 라벨 키. 미매핑이면 null을 돌려주고 호출부가 원문으로 폴백한다.
// 주문 레벨 취소 사유는 자유 입력이 섞일 수 있어 code는 string 그대로 받고 조회만 넓혀 읽는다.
export const resolvePathPickCancelReasonKey = (
  code: string | null | undefined
) =>
  code
    ? (CANCEL_REASON_LABEL_KEYS as Record<string, string>)[code] ?? null
    : null;

const HOUR_IN_MILLIS = 60 * 60 * 1000;
const MINUTE_IN_MILLIS = 60 * 1000;

// 자동 취소까지 남은 시간. 시안이 "777시간 17분"처럼 24를 넘겨 표기하므로
// 일/시로 쪼개지 않고 총 시간으로 계산한다.
export const getPathPickAutoCancelRemaining = (
  dueAt: string,
  nowMillis: number
) => {
  const remaining = new Date(dueAt).getTime() - nowMillis;
  if (!Number.isFinite(remaining) || remaining <= 0) {
    return { hours: 0, minutes: 0, isExpired: true };
  }
  return {
    hours: Math.floor(remaining / HOUR_IN_MILLIS),
    minutes: Math.floor(remaining / MINUTE_IN_MILLIS) % 60,
    isExpired: false,
  };
};

// 확정 후에는 서버가 계산한 금액(confirmed_amount)을 그대로 쓴다.
// 확정 전에는 매칭되는 옵션 금액 필드가 없어 요청 수량 × 판매가로 계산한다.
export const getPathPickOptionLineTotal = (
  option: PathPickOrderItemOption,
  salePrice: number,
  isQuantityConfirmed: boolean
) =>
  isQuantityConfirmed
    ? option.confirmedAmount
    : option.orderedQuantity * salePrice;

export const getPathPickItemSubtotal = (
  item: PathPickOrderItem,
  isQuantityConfirmed: boolean
) => (isQuantityConfirmed ? item.confirmedAmount : item.orderedAmount);

// 상태별 섹션 배치. 배치 차이를 이 함수 한 곳에 가둬 페이지에서 조건문이 뭉치지 않게 한다.
// 헤더/요약 스트립은 위치가 고정이라 여기 포함하지 않는다.
// 카드 골격(상품 → 입금/배송 2열)은 모든 상태가 공유하고, 상태별 차이는 위에 얹는 배너와
// 스텝퍼 유무뿐이다.
export const getPathPickOrderDetailSections = (
  order: PathPickOrder
): PathPickOrderDetailSectionKey[] => {
  if (order.status === "CANCELED") {
    return ["cancelBanner", "products", "remittanceShippingGrid"];
  }

  // 입금 요청은 안내 배너만 위에 더 붙이고, 카드 배치는 다른 상태와 동일하게 간다.
  if (order.status === "REMITTANCE_REQUESTED") {
    return ["banner", "progress", "products", "remittanceShippingGrid"];
  }

  return ["progress", "products", "remittanceShippingGrid"];
};
